import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Plus, Trash2, Save, Printer, ChevronDown, ChevronUp, ArrowLeft, Calendar, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditWorkOrderFull = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.includes('/admin/') ? '/admin' : '/workorder';
  const isWorkOrderPath = !location.pathname.includes('/admin/');
  const savedPath = isWorkOrderPath ? '/workorder#saved' : '/admin/saved';
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [expandedProcesses, setExpandedProcesses] = useState({
    process1: true,
    process2: false,
    process3: false,
    process4: false,
    process5: false,
    process6: false,
    process6a: false,
    process7: false,
    process8: false,
    process9: false,
    process10: false,
  });

  // Process Selection
  const [selectedProcesses, setSelectedProcesses] = useState({
    process1: false,
    process2: false,
    process3: false,
    process4: false,
    process5: false,
    process6: false,
    process6a: false,
    process7: false,
    process8: false,
    process9: false,
    process10: false,
  });

  // Header Data
  const [headerData, setHeaderData] = useState({
    customer_name: "",
    delivery_date: "",
    requested_by: "",
    required_quantity: "",
    item_name: "",
    reel_size: "",
    cutting_size: "",
    order_received_date: "",
    work_order_number: "",
    work_order_creation_date: "",
    po_number: "",
    dimension_unit: "mm",
    length: "",
    length_mm: "",
    breadth: "",
    breadth_mm: "",
    height: "",
    height_mm: "",
  });

  // Process States (initialize empty)
  const [process1, setProcess1] = useState({
    date: "",
    flute_type: "Narrow Flute",
    rows: [{ base_reel_id: "", base_weight: "", flute_reel_id: "", flute_weight: "" }]
  });

  const [process2, setProcess2] = useState({
    date: "",
    reel_no: "", bit_no: "", reel_size: "", cutting_size: "", sheet_counting: "", paper_counting: ""
  });

  const [process3, setProcess3] = useState({
    date: "",
    no_of_ply: "3", 
    top_paper_types: [{ top_paper_type: "", stock_id: "", qty: "" }], // Multiple top paper types with paper type selection
    reel_size: "", cutting_size: "", sheet_counting: ""
  });

  // Paper types for Process 3
  const PAPER_TYPES_P3 = [
    "Plain Kraft", "Plain Duplex", "Plain Gyt", "Plain Imported",
    "Printed Kraft", "Printed Duplex", "Printed Gyt", "Printed Imported"
  ];

  const [process4, setProcess4] = useState({
    date: "",
    length: "", breadth: "", height: "", reel_size: "", cutting_size: "", board_counting: ""
  });

  const [process5, setProcess5] = useState({
    date: "",
    length: "", breadth: "", height: "", reel_size: "", cutting_size: "",
    printing: "no", color: "", board_counting: ""
  });

  const [process6, setProcess6] = useState({
    date: "",
    length: "", breadth: "", height: "", reel_size: "", cutting_size: "",
    length_slotter: "", breadth_slotter: "", board_counting: ""
  });

  // Process 6A — Eccentric Slotter (added Feb 2026)
  const [process6a, setProcess6a] = useState({
    date: "", length: "", breadth: "", height: "", board_counting: ""
  });

  const [process7, setProcess7] = useState({
    date: "",
    length: "", breadth: "", height: "", board_counting: ""
  });

  const [process8, setProcess8] = useState({
    date: "",
    length: "", breadth: "", height: "", board_counting: ""
  });

  const [process9, setProcess9] = useState({
    date: "",
    length: "", breadth: "", height: "", board_counting: ""
  });

  const [process10, setProcess10] = useState({
    date: "",
    length: "", breadth: "", height: "", stitching_type: "Stitching Pin"
  });

  // Inventory for Process 3 searchable dropdown
  const [inventory, setInventory] = useState([]);
  const [p3MaterialSearchOpen, setP3MaterialSearchOpen] = useState(false);
  const [p3MaterialSearchTerm, setP3MaterialSearchTerm] = useState("");

  // Fetch existing work order data
  useEffect(() => {
    fetchWorkOrder();
    fetchInventory();
  }, [id]);

  const fetchInventory = async () => {
    try {
      const res = await axios.get(`${API}/inventory`);
      setInventory(res.data.filter(item => item.status === "available" || item.status === "return"));
    } catch (error) {
      console.error("Error fetching inventory:", error);
    }
  };

  const filteredP3Materials = inventory.filter(item => {
    const search = p3MaterialSearchTerm.toLowerCase();
    return (
      item.reel_id?.toLowerCase().includes(search) ||
      item.rm_name?.toLowerCase().includes(search) ||
      item.product?.toLowerCase().includes(search)
    );
  });

  const fetchWorkOrder = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/work-orders/${id}`);
      const wo = response.data;
      
      // Load header data
      if (wo.header) {
        setHeaderData(wo.header);
      }
      
      // Load processes data
      if (wo.processes) {
        const procs = wo.processes;
        
        // Helper to check if a process object has meaningful data
        const hasProcessData = (processData) => {
          if (!processData || typeof processData !== 'object') return false;
          // Check if any key (other than 'date') has a meaningful value
          return Object.entries(processData).some(([key, val]) => {
            if (key === 'date') return false; // Ignore empty date
            if (val === null || val === undefined || val === '') return false;
            if (Array.isArray(val)) {
              // For arrays like rows, top_paper_types - check if they have actual data
              return val.some(item => 
                typeof item === 'object' && Object.values(item).some(v => v && v !== '')
              );
            }
            return true;
          });
        };
        
        // Set selected processes based on which ones have actual data
        // Also include processes that have at least a date set
        const hasDataOrDate = (proc) => hasProcessData(proc) || (proc && proc.date && proc.date !== '');
        
        setSelectedProcesses({
          process1: hasDataOrDate(procs.process1),
          process2: hasDataOrDate(procs.process2),
          process3: hasDataOrDate(procs.process3),
          process4: hasDataOrDate(procs.process4),
          process5: hasDataOrDate(procs.process5),
          process6: hasDataOrDate(procs.process6),
          process6a: hasDataOrDate(procs.process6a),
          process7: hasDataOrDate(procs.process7),
          process8: hasDataOrDate(procs.process8),
          process9: hasDataOrDate(procs.process9),
          process10: hasDataOrDate(procs.process10),
        });
        
        // Also expand processes that have data
        setExpandedProcesses({
          process1: hasDataOrDate(procs.process1),
          process2: hasDataOrDate(procs.process2),
          process3: hasDataOrDate(procs.process3),
          process4: hasDataOrDate(procs.process4),
          process5: hasDataOrDate(procs.process5),
          process6: hasDataOrDate(procs.process6),
          process6a: hasDataOrDate(procs.process6a),
          process7: hasDataOrDate(procs.process7),
          process8: hasDataOrDate(procs.process8),
          process9: hasDataOrDate(procs.process9),
          process10: hasDataOrDate(procs.process10),
        });
        
        // Helper function to sanitize process data (replace nulls with defaults)
        const sanitizeProcess1 = (data) => ({
          date: data?.date || "",
          flute_type: data?.flute_type || "Narrow Flute",
          rows: Array.isArray(data?.rows) && data.rows.length > 0 
            ? data.rows 
            : [{ base_reel_id: "", base_weight: "", flute_reel_id: "", flute_weight: "" }]
        });
        
        const sanitizeSimpleProcess = (data, defaults) => {
          const result = { ...defaults };
          if (data) {
            Object.keys(defaults).forEach(key => {
              result[key] = data[key] ?? defaults[key];
            });
          }
          return result;
        };
        
        // Load each process if it exists with proper sanitization
        if (procs.process1) setProcess1(sanitizeProcess1(procs.process1));
        if (procs.process2) setProcess2(sanitizeSimpleProcess(procs.process2, {
          date: "", reel_no: "", bit_no: "", reel_size: "", cutting_size: "", sheet_counting: "", paper_counting: ""
        }));
        if (procs.process3) {
          const p3 = procs.process3;
          // Handle both old format (rm_name/top_paper_type string) and new format (top_paper_types array)
          let topPaperTypes = p3.top_paper_types;
          if (!topPaperTypes || !Array.isArray(topPaperTypes) || topPaperTypes.length === 0) {
            // Convert old format to new format
            topPaperTypes = [{ 
              top_paper_type: p3.top_paper_type || "", 
              stock_id: p3.rm_name || "", 
              qty: "" 
            }];
          }
          setProcess3({
            date: p3.date || "",
            no_of_ply: p3.no_of_ply || "3",
            top_paper_types: topPaperTypes,
            reel_size: p3.reel_size || "",
            cutting_size: p3.cutting_size || "",
            sheet_counting: p3.sheet_counting || ""
          });
        }
        if (procs.process4) setProcess4(sanitizeSimpleProcess(procs.process4, {
          date: "", length: "", breadth: "", height: "", reel_size: "", cutting_size: "", board_counting: ""
        }));
        if (procs.process5) setProcess5(sanitizeSimpleProcess(procs.process5, {
          date: "", length: "", breadth: "", height: "", reel_size: "", cutting_size: "", printing: "no", color: "", board_counting: ""
        }));
        if (procs.process6) setProcess6(sanitizeSimpleProcess(procs.process6, {
          date: "", length: "", breadth: "", height: "", reel_size: "", cutting_size: "", length_slotter: "", breadth_slotter: "", board_counting: ""
        }));
        if (procs.process6a) setProcess6a(sanitizeSimpleProcess(procs.process6a, {
          date: "", length: "", breadth: "", height: "", board_counting: ""
        }));
        if (procs.process7) setProcess7(sanitizeSimpleProcess(procs.process7, {
          date: "", length: "", breadth: "", height: "", board_counting: ""
        }));
        if (procs.process8) setProcess8(sanitizeSimpleProcess(procs.process8, {
          date: "", length: "", breadth: "", height: "", board_counting: ""
        }));
        if (procs.process9) setProcess9(sanitizeSimpleProcess(procs.process9, {
          date: "", length: "", breadth: "", height: "", board_counting: ""
        }));
        if (procs.process10) setProcess10(sanitizeSimpleProcess(procs.process10, {
          date: "", length: "", breadth: "", height: "", stitching_type: "Stitching Pin"
        }));
      }
      
      toast.success("Work order loaded successfully");
    } catch (error) {
      console.error("Error fetching work order:", error);
      toast.error("Failed to load work order");
      navigate(savedPath);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const workOrderData = {
        header: headerData,
        processes: {
          ...(selectedProcesses.process1 && { process1 }),
          ...(selectedProcesses.process2 && { process2 }),
          ...(selectedProcesses.process3 && { process3 }),
          ...(selectedProcesses.process4 && { process4 }),
          ...(selectedProcesses.process5 && { process5 }),
          ...(selectedProcesses.process6 && { process6 }),
          ...(selectedProcesses.process6a && { process6a }),
          ...(selectedProcesses.process7 && { process7 }),
          ...(selectedProcesses.process8 && { process8 }),
          ...(selectedProcesses.process9 && { process9 }),
          ...(selectedProcesses.process10 && { process10 }),
        }
      };

      await axios.put(`${API}/work-orders/${id}`, workOrderData);
      toast.success("Work Order updated successfully!");
      navigate(savedPath);
    } catch (error) {
      console.error("Error updating work order:", error);
      toast.error("Failed to update work order");
    } finally {
      setSaving(false);
    }
  };

  const toggleProcess = (processKey) => {
    setExpandedProcesses(prev => ({
      ...prev,
      [processKey]: !prev[processKey]
    }));
  };

  const handleClone = async () => {
    try {
      // Get the next work order number
      const allWOResponse = await axios.get(`${API}/work-orders`);
      const allWorkOrders = allWOResponse.data || [];
      
      let maxNumber = 0;
      allWorkOrders.forEach(wo => {
        const woNum = wo.header?.work_order_number || '';
        const match = woNum.match(/WONO-(\d+)/);
        if (match) {
          const num = parseInt(match[1], 10);
          if (num > maxNumber) maxNumber = num;
        }
      });
      
      const nextNumber = String(maxNumber + 1).padStart(3, '0');
      const newWONumber = `WONO-${nextNumber}`;
      
      navigate(`${basePath}#creation`, {
        state: {
          cloneData: {
            header: {
              ...headerData,
              work_order_number: newWONumber,
              work_order_creation_date: new Date().toISOString().split('T')[0],
            },
            processes: {
              process1, process2, process3, process4, process5,
              process6, process6a, process7, process8, process9, process10,
            },
            selected_processes: selectedProcesses,
          }
        }
      });
      
      toast.success(`Cloning to ${newWONumber}`);
    } catch (error) {
      console.error("Error cloning:", error);
      toast.error("Failed to clone");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black p-8 flex items-center justify-center">
        <div className="text-yellow-400 text-2xl">Loading work order...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-600 to-orange-600 p-6 shadow-2xl border-b-4 border-yellow-500">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-white">Edit Work Order</h1>
            <p className="text-yellow-100 mt-1">WO: {headerData.work_order_number}</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => navigate(`${basePath}/saved`)}
              variant="outline"
              className="border-white text-white hover:bg-white/20"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleClone}
              className="bg-green-600 hover:bg-green-700"
            >
              <Copy className="h-4 w-4 mr-2" />
              Clone
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Work Order Header Card */}
        <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-yellow-500 mb-6">
          <CardHeader className="bg-yellow-500/20 border-b-2 border-yellow-500">
            <CardTitle className="text-2xl text-yellow-400 flex items-center gap-2">
              Work Order Header
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-yellow-400">WO Number</Label>
                <Input
                  value={headerData.work_order_number}
                  onChange={(e) => setHeaderData({ ...headerData, work_order_number: e.target.value })}
                  className="bg-black border-yellow-500 text-white"
                />
              </div>
              <div>
                <Label className="text-yellow-400">Customer Name</Label>
                <Input
                  value={headerData.customer_name}
                  onChange={(e) => setHeaderData({ ...headerData, customer_name: e.target.value })}
                  className="bg-black border-yellow-500 text-white"
                />
              </div>
              <div>
                <Label className="text-yellow-400">Item Name</Label>
                <Input
                  value={headerData.item_name}
                  onChange={(e) => setHeaderData({ ...headerData, item_name: e.target.value })}
                  className="bg-black border-yellow-500 text-white"
                />
              </div>
              <div>
                <Label className="text-yellow-400">Required Quantity</Label>
                <Input
                  type="number"
                  value={headerData.required_quantity}
                  onChange={(e) => setHeaderData({ ...headerData, required_quantity: e.target.value })}
                  className="bg-black border-yellow-500 text-white"
                />
              </div>
              <div>
                <Label className="text-yellow-400">Delivery Date</Label>
                <div className="relative">
                  <Input
                    type="date"
                    value={headerData.delivery_date}
                    onChange={(e) => setHeaderData({ ...headerData, delivery_date: e.target.value })}
                    className="bg-black border-yellow-500 text-white pr-10 [color-scheme:dark]"
                  />
                  <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-yellow-400 pointer-events-none" />
                </div>
              </div>
              <div>
                <Label className="text-yellow-400">PO Number</Label>
                <Input
                  value={headerData.po_number}
                  onChange={(e) => setHeaderData({ ...headerData, po_number: e.target.value })}
                  className="bg-black border-yellow-500 text-white"
                />
              </div>
            </div>

            {/* Dimensions */}
            <div className="border-t border-yellow-500/30 pt-4">
              <h3 className="text-yellow-400 font-semibold mb-3">Box Dimensions</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-yellow-400">Length ({headerData.dimension_unit})</Label>
                  <Input
                    value={headerData.length}
                    onChange={(e) => setHeaderData({ ...headerData, length: e.target.value })}
                    className="bg-black border-yellow-500 text-white"
                  />
                </div>
                <div>
                  <Label className="text-yellow-400">Breadth ({headerData.dimension_unit})</Label>
                  <Input
                    value={headerData.breadth}
                    onChange={(e) => setHeaderData({ ...headerData, breadth: e.target.value })}
                    className="bg-black border-yellow-500 text-white"
                  />
                </div>
                <div>
                  <Label className="text-yellow-400">Height ({headerData.dimension_unit})</Label>
                  <Input
                    value={headerData.height}
                    onChange={(e) => setHeaderData({ ...headerData, height: e.target.value })}
                    className="bg-black border-yellow-500 text-white"
                  />
                </div>
                <div>
                  <Label className="text-yellow-400">Unit</Label>
                  <Select
                    value={headerData.dimension_unit}
                    onValueChange={(value) => setHeaderData({ ...headerData, dimension_unit: value })}
                  >
                    <SelectTrigger className="bg-black border-yellow-500 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mm">Millimeters (mm)</SelectItem>
                      <SelectItem value="inch">Inches</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Process Selection Card */}
        <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-yellow-500 mb-6">
          <CardHeader className="bg-yellow-500/20 border-b-2 border-yellow-500">
            <CardTitle className="text-2xl text-yellow-400 flex justify-between items-center">
              <span>Select Processes to Edit</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const allSelected = Object.values(selectedProcesses).every(v => v);
                  const newState = Object.keys(selectedProcesses).reduce((acc, key) => {
                    acc[key] = !allSelected;
                    return acc;
                  }, {});
                  setSelectedProcesses(newState);
                  setExpandedProcesses(newState);
                }}
                className="border-yellow-500 text-yellow-400 hover:bg-yellow-500/20"
              >
                {Object.values(selectedProcesses).every(v => v) ? 'Deselect All' : 'Edit All Processes'}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {Object.keys(selectedProcesses).map((key, index) => (
                <div key={key} className="flex items-center space-x-2">
                  <Checkbox
                    id={key}
                    checked={selectedProcesses[key]}
                    onCheckedChange={(checked) =>
                      setSelectedProcesses({ ...selectedProcesses, [key]: checked })
                    }
                    className="border-yellow-500"
                  />
                  <Label htmlFor={key} className="text-white cursor-pointer">
                    P{index + 1}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Individual Process Cards */}
        {selectedProcesses.process1 && (
          <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-yellow-500 mb-4">
            <CardHeader
              className="bg-yellow-500/20 border-b-2 border-yellow-500 cursor-pointer"
              onClick={() => toggleProcess('process1')}
            >
              <CardTitle className="text-xl text-yellow-400 flex justify-between items-center">
                <span>P1 - CORRUGATION Machine</span>
                {expandedProcesses.process1 ? <ChevronUp /> : <ChevronDown />}
              </CardTitle>
            </CardHeader>
            {expandedProcesses.process1 && (
              <CardContent className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-yellow-400">Date</Label>
                    <div className="relative">
                      <Input
                        type="date"
                        value={process1.date}
                        onChange={(e) => setProcess1({ ...process1, date: e.target.value })}
                        className="bg-black border-yellow-500 text-white pr-10 [color-scheme:dark]"
                      />
                      <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-yellow-400 pointer-events-none" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-yellow-400">Type of Flute</Label>
                    <Select
                      value={process1.flute_type}
                      onValueChange={(value) => setProcess1({ ...process1, flute_type: value })}
                    >
                      <SelectTrigger className="bg-black border-yellow-500 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Narrow Flute">Narrow Flute</SelectItem>
                        <SelectItem value="E Flute">E Flute</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Dynamic Rows */}
                {process1.rows && process1.rows.map((row, index) => (
                  <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 border border-yellow-500/30 rounded">
                    <div>
                      <Label className="text-yellow-400">Base Reel ID</Label>
                      <Input
                        value={row.base_reel_id}
                        onChange={(e) => {
                          const newRows = [...process1.rows];
                          newRows[index].base_reel_id = e.target.value;
                          setProcess1({ ...process1, rows: newRows });
                        }}
                        className="bg-black border-yellow-500 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-yellow-400">Base Weight (kg)</Label>
                      <Input
                        value={row.base_weight}
                        onChange={(e) => {
                          const newRows = [...process1.rows];
                          newRows[index].base_weight = e.target.value;
                          setProcess1({ ...process1, rows: newRows });
                        }}
                        className="bg-black border-yellow-500 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-yellow-400">Flute Reel ID</Label>
                      <Input
                        value={row.flute_reel_id}
                        onChange={(e) => {
                          const newRows = [...process1.rows];
                          newRows[index].flute_reel_id = e.target.value;
                          setProcess1({ ...process1, rows: newRows });
                        }}
                        className="bg-black border-yellow-500 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-yellow-400">Flute Weight (kg)</Label>
                      <Input
                        value={row.flute_weight}
                        onChange={(e) => {
                          const newRows = [...process1.rows];
                          newRows[index].flute_weight = e.target.value;
                          setProcess1({ ...process1, rows: newRows });
                        }}
                        className="bg-black border-yellow-500 text-white"
                      />
                    </div>
                  </div>
                ))}
                
                <div className="flex gap-2">
                  <Button
                    type="button"
                    onClick={() => setProcess1({
                      ...process1,
                      rows: [...process1.rows, { base_reel_id: "", base_weight: "", flute_reel_id: "", flute_weight: "" }]
                    })}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Row
                  </Button>
                  {process1.rows.length > 1 && (
                    <Button
                      type="button"
                      onClick={() => setProcess1({
                        ...process1,
                        rows: process1.rows.slice(0, -1)
                      })}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remove Last Row
                    </Button>
                  )}
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Process 2 */}
        {selectedProcesses.process2 && (
          <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-yellow-500 mb-4">
            <CardHeader
              className="bg-yellow-500/20 border-b-2 border-yellow-500 cursor-pointer"
              onClick={() => toggleProcess('process2')}
            >
              <CardTitle className="text-xl text-yellow-400 flex justify-between items-center">
                <span>P2 - Sheet / Paper Cutting</span>
                {expandedProcesses.process2 ? <ChevronUp /> : <ChevronDown />}
              </CardTitle>
            </CardHeader>
            {expandedProcesses.process2 && (
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-yellow-400">Date</Label>
                    <div className="relative">
                      <Input
                        type="date"
                        value={process2.date}
                        onChange={(e) => setProcess2({ ...process2, date: e.target.value })}
                        className="bg-black border-yellow-500 text-white pr-10 [color-scheme:dark]"
                      />
                      <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-yellow-400 pointer-events-none" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-yellow-400">Reel No.</Label>
                    <Input
                      value={process2.reel_no}
                      onChange={(e) => setProcess2({ ...process2, reel_no: e.target.value })}
                      className="bg-black border-yellow-500 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-yellow-400">Bit No.</Label>
                    <Input
                      value={process2.bit_no}
                      onChange={(e) => setProcess2({ ...process2, bit_no: e.target.value })}
                      className="bg-black border-yellow-500 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-yellow-400">Reel Size</Label>
                    <Input
                      value={process2.reel_size}
                      onChange={(e) => setProcess2({ ...process2, reel_size: e.target.value })}
                      className="bg-black border-yellow-500 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-yellow-400">Cutting Size</Label>
                    <Input
                      value={process2.cutting_size}
                      onChange={(e) => setProcess2({ ...process2, cutting_size: e.target.value })}
                      className="bg-black border-yellow-500 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-yellow-400">Sheet Counting</Label>
                    <Input
                      value={process2.sheet_counting}
                      onChange={(e) => setProcess2({ ...process2, sheet_counting: e.target.value })}
                      className="bg-black border-yellow-500 text-white"
                    />
                  </div>
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Process 3 - Sheet Pasting */}
        {selectedProcesses.process3 && (
          <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-yellow-500 mb-4">
            <CardHeader
              className="bg-yellow-500/20 border-b-2 border-yellow-500 cursor-pointer"
              onClick={() => toggleProcess('process3')}
            >
              <CardTitle className="text-xl text-yellow-400 flex justify-between items-center">
                <span>P3 - Sheet Pasting</span>
                {expandedProcesses.process3 ? <ChevronUp /> : <ChevronDown />}
              </CardTitle>
            </CardHeader>
            {expandedProcesses.process3 && (
              <CardContent className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-yellow-400">Date</Label>
                    <Input type="date" value={process3.date} onChange={(e) => setProcess3({ ...process3, date: e.target.value })} className="bg-black border-yellow-500 text-white pr-10 [color-scheme:dark]" />
                  </div>
                  <div>
                    <Label className="text-yellow-400">No of PLY</Label>
                    <Input value={process3.no_of_ply} onChange={(e) => setProcess3({ ...process3, no_of_ply: e.target.value })} className="bg-black border-yellow-500 text-white" />
                  </div>
                  <div>
                    <Label className="text-yellow-400">Reel Size</Label>
                    <Input value={process3.reel_size} onChange={(e) => setProcess3({ ...process3, reel_size: e.target.value })} className="bg-black border-yellow-500 text-white" />
                  </div>
                  <div>
                    <Label className="text-yellow-400">Cutting Size</Label>
                    <Input value={process3.cutting_size} onChange={(e) => setProcess3({ ...process3, cutting_size: e.target.value })} className="bg-black border-yellow-500 text-white" />
                  </div>
                  <div>
                    <Label className="text-yellow-400">Sheet Counting</Label>
                    <Input value={process3.sheet_counting} onChange={(e) => setProcess3({ ...process3, sheet_counting: e.target.value })} className="bg-black border-yellow-500 text-white" />
                  </div>
                </div>
                
                {/* Multiple Top Paper Types */}
                <div className="border-t border-yellow-500/30 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-yellow-400 text-lg">Top Paper / Printed Paper Materials</Label>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => setProcess3({
                        ...process3,
                        top_paper_types: [...(process3.top_paper_types || []), { top_paper_type: "", stock_id: "", qty: "" }]
                      })}
                      className="bg-yellow-500 hover:bg-yellow-600 text-black h-8"
                    >
                      <Plus className="h-4 w-4 mr-1" /> Add Paper
                    </Button>
                  </div>
                  
                  {(process3.top_paper_types || [{ top_paper_type: "", stock_id: "", qty: "" }]).map((paper, idx) => {
                    // Filter inventory based on selected top paper type
                    const filteredInventoryForPaper = paper.top_paper_type 
                      ? inventory.filter(item => 
                          item.product?.toLowerCase() === paper.top_paper_type.toLowerCase()
                        )
                      : inventory;
                    
                    return (
                      <div key={idx} className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg mb-2">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-yellow-300 text-sm font-medium">Paper #{idx + 1}</span>
                          {(process3.top_paper_types?.length || 1) > 1 && (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => setProcess3({
                                ...process3,
                                top_paper_types: process3.top_paper_types.filter((_, i) => i !== idx)
                              })}
                              className="text-red-400 hover:text-red-300 h-6 w-6 p-0"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          {/* Top Paper Type Dropdown */}
                          <div>
                            <Label className="text-gray-400 text-xs">Top Paper Type</Label>
                            <Select
                              value={paper.top_paper_type || ""}
                              onValueChange={(value) => {
                                const newPapers = [...(process3.top_paper_types || [])];
                                newPapers[idx] = { 
                                  ...newPapers[idx], 
                                  top_paper_type: value,
                                  stock_id: "" // Reset stock_id when paper type changes
                                };
                                setProcess3({ ...process3, top_paper_types: newPapers });
                              }}
                            >
                              <SelectTrigger className="bg-black border-yellow-500/50 text-white mt-1 h-9">
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                              <SelectContent className="bg-gray-900 border-yellow-500">
                                {PAPER_TYPES_P3.map(type => (
                                  <SelectItem key={type} value={type} className="text-white hover:bg-yellow-500/20">
                                    {type}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          {/* Stock ID Dropdown - Only shows items matching selected paper type */}
                          <div>
                            <Label className="text-gray-400 text-xs">Stock ID</Label>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  className="w-full justify-between bg-black border-yellow-500/50 text-white mt-1 h-9"
                                  disabled={!paper.top_paper_type}
                                >
                                  {paper.stock_id || (paper.top_paper_type ? "Select stock..." : "Select type first")}
                                  <ChevronDown className="h-4 w-4 opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-[400px] p-0 bg-gray-900 border-yellow-500">
                                <Command className="bg-gray-900" shouldFilter={false}>
                                  <CommandInput placeholder="Search stock ID..." />
                                  <CommandList className="max-h-60">
                                    <CommandEmpty>
                                      {filteredInventoryForPaper.length === 0 
                                        ? `No ${paper.top_paper_type} items in inventory` 
                                        : "No materials found"}
                                    </CommandEmpty>
                                    <CommandGroup>
                                      {filteredInventoryForPaper.map(item => (
                                        <CommandItem
                                          key={item.id}
                                          onSelect={() => {
                                            const newPapers = [...(process3.top_paper_types || [])];
                                            newPapers[idx] = { 
                                              ...newPapers[idx], 
                                              stock_id: item.reel_id
                                            };
                                            setProcess3({ ...process3, top_paper_types: newPapers });
                                          }}
                                          className="text-white hover:bg-yellow-500/20 cursor-pointer"
                                        >
                                          <Check className={cn("mr-2 h-4 w-4", paper.stock_id === item.reel_id ? "opacity-100" : "opacity-0")} />
                                          <div className="flex flex-col">
                                            <span className="text-cyan-400 font-bold">{item.reel_id}</span>
                                            <span className="text-xs text-gray-400">{item.rm_name} | {item.reel_size}" | {item.reel_weight} Kg</span>
                                          </div>
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </div>
                          <div>
                            <Label className="text-gray-400 text-xs">Qty (Nos)</Label>
                            <Input
                              type="number"
                              value={paper.qty}
                              onChange={(e) => {
                                const newPapers = [...(process3.top_paper_types || [])];
                                newPapers[idx] = { ...newPapers[idx], qty: e.target.value };
                                setProcess3({ ...process3, top_paper_types: newPapers });
                              }}
                              className="bg-black border-yellow-500/50 text-white mt-1 h-9"
                              placeholder="Qty"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <p className="text-xs text-gray-500 mt-2">
                    Select a Top Paper Type first, then choose from available Stock IDs matching that type.
                  </p>
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Process 4-10 with full field implementations */}
        {selectedProcesses.process4 && (
          <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-yellow-500 mb-4">
            <CardHeader className="bg-yellow-500/20 border-b-2 border-yellow-500 cursor-pointer" onClick={() => toggleProcess('process4')}>
              <CardTitle className="text-xl text-yellow-400 flex justify-between items-center">
                <span>P4 - Thin Blade Slitter Scorer</span>
                {expandedProcesses.process4 ? <ChevronUp /> : <ChevronDown />}
              </CardTitle>
            </CardHeader>
            {expandedProcesses.process4 && (
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><Label className="text-yellow-400">Date</Label><Input type="date" value={process4.date} onChange={(e) => setProcess4({ ...process4, date: e.target.value })} className="bg-black border-yellow-500 text-white pr-10 [color-scheme:dark]" /></div>
                  <div><Label className="text-yellow-400">Length</Label><Input value={process4.length} onChange={(e) => setProcess4({ ...process4, length: e.target.value })} className="bg-black border-yellow-500 text-white" /></div>
                  <div><Label className="text-yellow-400">Breadth</Label><Input value={process4.breadth} onChange={(e) => setProcess4({ ...process4, breadth: e.target.value })} className="bg-black border-yellow-500 text-white" /></div>
                  <div><Label className="text-yellow-400">Height</Label><Input value={process4.height} onChange={(e) => setProcess4({ ...process4, height: e.target.value })} className="bg-black border-yellow-500 text-white" /></div>
                  <div><Label className="text-yellow-400">Board Counting</Label><Input value={process4.board_counting} onChange={(e) => setProcess4({ ...process4, board_counting: e.target.value })} className="bg-black border-yellow-500 text-white" /></div>
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {selectedProcesses.process5 && (
          <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-yellow-500 mb-4">
            <CardHeader className="bg-yellow-500/20 border-b-2 border-yellow-500 cursor-pointer" onClick={() => toggleProcess('process5')}>
              <CardTitle className="text-xl text-yellow-400 flex justify-between items-center">
                <span>P5 - 4 Bar Rotary Creasing</span>
                {expandedProcesses.process5 ? <ChevronUp /> : <ChevronDown />}
              </CardTitle>
            </CardHeader>
            {expandedProcesses.process5 && (
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><Label className="text-yellow-400">Date</Label><Input type="date" value={process5.date} onChange={(e) => setProcess5({ ...process5, date: e.target.value })} className="bg-black border-yellow-500 text-white pr-10 [color-scheme:dark]" /></div>
                  <div><Label className="text-yellow-400">Printing</Label><Select value={process5.printing} onValueChange={(value) => setProcess5({ ...process5, printing: value })}><SelectTrigger className="bg-black border-yellow-500 text-white"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent></Select></div>
                  <div><Label className="text-yellow-400">Color</Label><Input value={process5.color} onChange={(e) => setProcess5({ ...process5, color: e.target.value })} className="bg-black border-yellow-500 text-white" /></div>
                  <div><Label className="text-yellow-400">Board Counting</Label><Input value={process5.board_counting} onChange={(e) => setProcess5({ ...process5, board_counting: e.target.value })} className="bg-black border-yellow-500 text-white" /></div>
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {selectedProcesses.process6 && (
          <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-yellow-500 mb-4">
            <CardHeader className="bg-yellow-500/20 border-b-2 border-yellow-500 cursor-pointer" onClick={() => toggleProcess('process6')}>
              <CardTitle className="text-xl text-yellow-400 flex justify-between items-center">
                <span>P6 - RS4 Single Color Printing & Slotting</span>
                {expandedProcesses.process6 ? <ChevronUp /> : <ChevronDown />}
              </CardTitle>
            </CardHeader>
            {expandedProcesses.process6 && (
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><Label className="text-yellow-400">Date</Label><Input type="date" value={process6.date} onChange={(e) => setProcess6({ ...process6, date: e.target.value })} className="bg-black border-yellow-500 text-white pr-10 [color-scheme:dark]" /></div>
                  <div><Label className="text-yellow-400">Length</Label><Input value={process6.length} onChange={(e) => setProcess6({ ...process6, length: e.target.value })} className="bg-black border-yellow-500 text-white" /></div>
                  <div><Label className="text-yellow-400">Breadth</Label><Input value={process6.breadth} onChange={(e) => setProcess6({ ...process6, breadth: e.target.value })} className="bg-black border-yellow-500 text-white" /></div>
                  <div><Label className="text-yellow-400">Height</Label><Input value={process6.height} onChange={(e) => setProcess6({ ...process6, height: e.target.value })} className="bg-black border-yellow-500 text-white" /></div>
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {selectedProcesses.process6a && (
          <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-blue-500 mb-4">
            <CardHeader className="bg-blue-500/20 border-b-2 border-blue-500 cursor-pointer" onClick={() => toggleProcess('process6a')}>
              <CardTitle className="text-xl text-blue-400 flex justify-between items-center">
                <span>P6A - Eccentric Slotter</span>
                {expandedProcesses.process6a ? <ChevronUp /> : <ChevronDown />}
              </CardTitle>
            </CardHeader>
            {expandedProcesses.process6a && (
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div><Label className="text-blue-400">Date</Label><Input type="date" value={process6a.date} onChange={(e) => setProcess6a({ ...process6a, date: e.target.value })} className="bg-black border-blue-500 text-white pr-10 [color-scheme:dark]" /></div>
                  <div><Label className="text-blue-400">Length</Label><Input value={process6a.length} onChange={(e) => setProcess6a({ ...process6a, length: e.target.value })} className="bg-black border-blue-500 text-white" /></div>
                  <div><Label className="text-blue-400">Breadth</Label><Input value={process6a.breadth} onChange={(e) => setProcess6a({ ...process6a, breadth: e.target.value })} className="bg-black border-blue-500 text-white" /></div>
                  <div><Label className="text-blue-400">Height</Label><Input value={process6a.height} onChange={(e) => setProcess6a({ ...process6a, height: e.target.value })} className="bg-black border-blue-500 text-white" /></div>
                  <div><Label className="text-blue-400">Board Counting</Label><Input type="number" value={process6a.board_counting} onChange={(e) => setProcess6a({ ...process6a, board_counting: e.target.value })} className="bg-black border-blue-500 text-white" /></div>
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {selectedProcesses.process7 && (
          <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-yellow-500 mb-4">
            <CardHeader className="bg-yellow-500/20 border-b-2 border-yellow-500 cursor-pointer" onClick={() => toggleProcess('process7')}>
              <CardTitle className="text-xl text-yellow-400 flex justify-between items-center">
                <span>P7 - Flat bed Punching</span>
                {expandedProcesses.process7 ? <ChevronUp /> : <ChevronDown />}
              </CardTitle>
            </CardHeader>
            {expandedProcesses.process7 && (
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><Label className="text-yellow-400">Date</Label><Input type="date" value={process7.date} onChange={(e) => setProcess7({ ...process7, date: e.target.value })} className="bg-black border-yellow-500 text-white pr-10 [color-scheme:dark]" /></div>
                  <div><Label className="text-yellow-400">Die ID</Label><Input value={process7.die_id} onChange={(e) => setProcess7({ ...process7, die_id: e.target.value })} className="bg-black border-yellow-500 text-white" /></div>
                  <div><Label className="text-yellow-400">Counting</Label><Input value={process7.counting} onChange={(e) => setProcess7({ ...process7, counting: e.target.value })} className="bg-black border-yellow-500 text-white" /></div>
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {selectedProcesses.process8 && (
          <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-yellow-500 mb-4">
            <CardHeader className="bg-yellow-500/20 border-b-2 border-yellow-500 cursor-pointer" onClick={() => toggleProcess('process8')}>
              <CardTitle className="text-xl text-yellow-400 flex justify-between items-center">
                <span>P8 - Rotary Die Punching</span>
                {expandedProcesses.process8 ? <ChevronUp /> : <ChevronDown />}
              </CardTitle>
            </CardHeader>
            {expandedProcesses.process8 && (
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><Label className="text-yellow-400">Date</Label><Input type="date" value={process8.date} onChange={(e) => setProcess8({ ...process8, date: e.target.value })} className="bg-black border-yellow-500 text-white pr-10 [color-scheme:dark]" /></div>
                  <div><Label className="text-yellow-400">Die ID</Label><Input value={process8.die_id} onChange={(e) => setProcess8({ ...process8, die_id: e.target.value })} className="bg-black border-yellow-500 text-white" /></div>
                  <div><Label className="text-yellow-400">Printing</Label><Select value={process8.printing} onValueChange={(value) => setProcess8({ ...process8, printing: value })}><SelectTrigger className="bg-black border-yellow-500 text-white"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent></Select></div>
                  <div><Label className="text-yellow-400">Color</Label><Input value={process8.color} onChange={(e) => setProcess8({ ...process8, color: e.target.value })} className="bg-black border-yellow-500 text-white" /></div>
                  <div><Label className="text-yellow-400">Counting</Label><Input value={process8.counting} onChange={(e) => setProcess8({ ...process8, counting: e.target.value })} className="bg-black border-yellow-500 text-white" /></div>
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {selectedProcesses.process9 && (
          <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-yellow-500 mb-4">
            <CardHeader className="bg-yellow-500/20 border-b-2 border-yellow-500 cursor-pointer" onClick={() => toggleProcess('process9')}>
              <CardTitle className="text-xl text-yellow-400 flex justify-between items-center">
                <span>P9 - Calico Pasting</span>
                {expandedProcesses.process9 ? <ChevronUp /> : <ChevronDown />}
              </CardTitle>
            </CardHeader>
            {expandedProcesses.process9 && (
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><Label className="text-yellow-400">Date</Label><Input type="date" value={process9.date} onChange={(e) => setProcess9({ ...process9, date: e.target.value })} className="bg-black border-yellow-500 text-white pr-10 [color-scheme:dark]" /></div>
                  <div><Label className="text-yellow-400">Calico Color</Label><Input value={process9.calico_color} onChange={(e) => setProcess9({ ...process9, calico_color: e.target.value })} className="bg-black border-yellow-500 text-white" /></div>
                  <div><Label className="text-yellow-400">Number of Calico</Label><Input value={process9.number_of_calico} onChange={(e) => setProcess9({ ...process9, number_of_calico: e.target.value })} className="bg-black border-yellow-500 text-white" /></div>
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {selectedProcesses.process10 && (
          <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-yellow-500 mb-4">
            <CardHeader className="bg-yellow-500/20 border-b-2 border-yellow-500 cursor-pointer" onClick={() => toggleProcess('process10')}>
              <CardTitle className="text-xl text-yellow-400 flex justify-between items-center">
                <span>P10 - Stitching & Bundling</span>
                {expandedProcesses.process10 ? <ChevronUp /> : <ChevronDown />}
              </CardTitle>
            </CardHeader>
            {expandedProcesses.process10 && (
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><Label className="text-yellow-400">Date</Label><Input type="date" value={process10.date} onChange={(e) => setProcess10({ ...process10, date: e.target.value })} className="bg-black border-yellow-500 text-white pr-10 [color-scheme:dark]" /></div>
                  <div><Label className="text-yellow-400">Boxes in Bundle</Label><Input value={process10.boxes_in_bundle} onChange={(e) => setProcess10({ ...process10, boxes_in_bundle: e.target.value })} className="bg-black border-yellow-500 text-white" /></div>
                  <div><Label className="text-yellow-400">Stitching Type</Label><Select value={process10.stitching_type} onValueChange={(value) => setProcess10({ ...process10, stitching_type: value })}><SelectTrigger className="bg-black border-yellow-500 text-white"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Single">Single</SelectItem><SelectItem value="Double">Double</SelectItem></SelectContent></Select></div>
                  <div><Label className="text-yellow-400">Total Bundles</Label><Input value={process10.total_bundles} onChange={(e) => setProcess10({ ...process10, total_bundles: e.target.value })} className="bg-black border-yellow-500 text-white" /></div>
                </div>
              </CardContent>
            )}
          </Card>
        )}


        {/* Save Button at Bottom */}
        <div className="flex justify-end gap-3 mt-6">
          <Button
            onClick={() => navigate(`${basePath}/saved`)}
            variant="outline"
            className="border-yellow-500 text-yellow-400"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-green-600 hover:bg-green-700"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditWorkOrderFull;
